import { AllegatoS3 } from './allegati.model';
import { EtichettaMessaggioRTO } from './etichetta-messaggio.model';

export type MailPriority = 'STANDARD' | 'MEDIUM' | 'HIGH';

export type MailType =
  | 'INBOX' | 'OUTBOX' | 'DRAFT' | 'ARCHIVED'
  | 'ACCETTAZIONE' | 'NON_ACCETTAZIONE' | 'AVVENUTA_CONSEGNA' | 'ERRORE_CONSEGNA'
  | 'PREAVVISO_ERRORE_CONSEGNA' | 'RILEVAZIONE_VIRUS' | 'PRESA_IN_CARICO' | 'SCONOSCIUTA';

export interface InoltroMessaggio {
  id: number;
  emailDestinatario: string;
  idGruppo?: string | null;
  messaggioInoltratoId?: number | null;
  corpoMessaggio?: string | null;
  statoInoltro: 'NON_INOLTRATO' | 'INOLTRO_COMPLETATO' | 'INOLTRO_FALLITO';
  createdAt: string;
  updatedAt: string;
}

export type TipologiaInvio = 'TO' | 'CC';

export type CampoRicerca = 'MITTENTE' | 'DESTINATARIO' | 'OGGETTO' | 'CORPO' | 'ETICHETTA';

/** Suggerimento mittente per la prima sezione del pannello di ricerca. */
export interface MittenteSuggerito {
  mittente: string;
  nomeMittente: string | null;
}

/**
 * Destinatario di un messaggio: i dati del contatto in rubrica piu' la veste in cui
 * compare su quel messaggio. Arriva dalla tabella di relazione messaggi_destinatari.
 */
export interface DestinatarioMail {
  contattoId: number;
  email: string;
  nomeCompleto: string | null;
  tipologiaInvio: TipologiaInvio;
}

/** Indirizzi dei destinatari, opzionalmente filtrati per tipologia. */
export function emailDestinatari(mail: Mail, tipologia?: TipologiaInvio): string[] {
  return (mail.destinatari ?? [])
    .filter(d => !tipologia || d.tipologiaInvio === tipologia)
    .map(d => d.email);
}

/**
 * Etichetta leggibile dei destinatari: preferisce il nome del contatto in rubrica e
 * ripiega sull'email quando il contatto non ha un nome.
 */
export function etichettaDestinatari(mail: Mail, tipologia?: TipologiaInvio): string {
  return (mail.destinatari ?? [])
    .filter(d => !tipologia || d.tipologiaInvio === tipologia)
    .map(d => d.nomeCompleto?.trim() || d.email)
    .join(', ');
}

export interface Mail {
  id: number;
  corpo: string;
  oggetto: string;
  tipo: MailType;
  priorita: MailPriority;
  /** Destinatari diretti e in copia. Opzionale: puo' mancare su record storici. */
  destinatari?: DestinatarioMail[];
  mittente: string;
  nomeMittente: string | null;
  idRiferimento: string;
  /**
   * Se il messaggio è una ricevuta, l'idRiferimento della PEC che attesta; assente/null per le
   * comunicazioni. È questa la colonna che lega una ricevuta alla spedizione: `idRiferimento`
   * porta il Message-ID del gestore.
   */
  idRiferimentoAssociato?: string | null;
  dataComunicazione: string;
  inLavorazione: boolean;
  uuidLock?: string;
  lockDate?: string;
  letta: boolean;
  /** Etichetta associata al messaggio. Assente/null quando non è ancora stato etichettato. */
  etichetta?: EtichettaMessaggioRTO | null;
  allegati?: any[];
  inoltri?: InoltroMessaggio[];
  inoltrata?: boolean;
  /** Solo bozze di inoltro: id della PEC da cui nasce la bozza. */
  messaggioOriginaleId?: number | null;
  createdAt: string;
  updatedAt: string;
  casellaId: number;
}

export interface ICreateEmail {
  corpo: string;
  oggetto: string;
  tipo: string;
  priorita: MailPriority;
  /** Indirizzi dei destinatari diretti: il backend li risolve in contatti di rubrica. */
  destinatari: string[];
  cc?: string[];
  mittente: string;
  nomeMittente?: string;
  idRiferimento?: string;
  dataComunicazione: string;
  allegati?: AllegatoS3[];
  /** Solo bozze di inoltro: id della PEC da cui nasce la bozza. */
  messaggioOriginaleId?: number | null;
}

export interface IUpdateEmail {
  corpo?: string;
  oggetto?: string;
  tipo?: string;
  priorita?: MailPriority;
  destinatari?: string[];
  cc?: string[];
  ccn?: string[];
  mittente?: string;
  idRiferimento?: string;
  dataComunicazione?: string;
  allegati?: AllegatoS3[];
}

export interface IZipExport {
  idRiferimento: string;
  zipFile: any;
}

/**
 * Ricevute PEC: record con `idRiferimentoAssociato` uguale all'idRiferimento della PEC spedita.
 * Non hanno lo stesso idRiferimento della PEC — ognuna porta il Message-ID del gestore.
 */
export type TipoRicevuta = 'ACCETTAZIONE' | 'AVVENUTA_CONSEGNA';

/**
 * Se il messaggio di cui si chiedono le ricevute e' stato spedito direttamente o e' nato da un
 * inoltro. Le ricevute restituite sono sempre e solo quelle di quel messaggio: questo campo dice
 * di che spedizione si sta guardando la cronologia.
 */
export type OrigineRicevuta = 'INVIO' | 'INOLTRO';

export interface Ricevuta {
  id: number;
  tipo: TipoRicevuta;
  dataComunicazione: string;
  origine: OrigineRicevuta;
}

/**
 * Il messaggio attestato da una ricevuta, nel minimo che serve ad aprirlo: `id` per il link e
 * `tipo` per sapere in quale sezione aprirlo. Il backend risale dall'`idRiferimentoAssociato`
 * della ricevuta e restituisce sempre una comunicazione, quindi `OUTBOX` o `ARCHIVED`.
 */
export interface MessaggioOriginale {
  id: number;
  idRiferimento: string;
  tipo: MailType;
}